import type { FC, ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import LoginPage from '../pages/LoginPage';
import ModelsListPage from '../pages/ModelsListPage';
import ModelDetailPage from '../pages/ModelDetailPage';
import ModelEditPage from '../pages/ModelEditPage';
import MetaPage from '../pages/MetaPage';
import FileUploadPage from '../pages/FileUploadPage';
import FeedPage from '../pages/FeedPage';
import FeedModelDetailPage from '../pages/FeedModelDetailPage';
import PpcPage from '../pages/PpcPage';
import PhotoCleanupPage from '../pages/PhotoCleanupPage';
import BlogPostsPage from '../pages/blog/BlogPostsPage';
import BlogPostEditorPage from '../pages/blog/BlogPostEditorPage';
import ModelPinsPage from '../pages/ModelPinsPage';
import AnalyticsDashboardPage from '../pages/AnalyticsDashboardPage';
import TrafficPage from '../pages/TrafficPage';
import ClicksByPageOverviewPage from '../pages/ClicksByPageOverviewPage';
import CpcByPagePage from '../pages/CpcByPagePage';
import CpcOverviewPage from '../pages/CpcOverviewPage';
import CpcOfOtComparePage from '../pages/CpcOfOtComparePage';
import BacklogPage from '../pages/backlog/BacklogPage';
import PageVersionsPage from '../pages/PageVersionsPage';
import { useAuth } from '../context/AuthContext';

const Protected: FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />
  },
  {
    path: '/',
    element: (
      <Protected>
        <AppLayout />
      </Protected>
    ),
    children: [
      { index: true, element: <Navigate to="/models" replace /> },

      // Discovery
      { path: 'models',              element: <ModelsListPage /> },
      { path: 'models/:id/edit',     element: <ModelEditPage /> },
      { path: 'models/:id',          element: <ModelDetailPage /> },
      { path: 'feed',                element: <FeedPage /> },
      { path: 'feed/:username',      element: <FeedModelDetailPage /> },
      { path: 'pins',                element: <ModelPinsPage /> },

      // Operations
      { path: 'ppc',                 element: <PpcPage /> },
      { path: 'meta',                element: <MetaPage /> },
      { path: 'page-versions',       element: <PageVersionsPage /> },
      { path: 'photo-cleanup',       element: <PhotoCleanupPage /> },

      // Analytics
      { path: 'analytics',           element: <AnalyticsDashboardPage /> },
      { path: 'traffic',             element: <TrafficPage /> },
      { path: 'traffic/by-page',     element: <ClicksByPageOverviewPage /> },
      { path: 'cpc',                 element: <CpcByPagePage /> },
      { path: 'cpc/overview',        element: <CpcOverviewPage /> },
      { path: 'cpc/of-ot',           element: <CpcOfOtComparePage /> },

      // Content
      {
        path: 'blog',
        children: [
          { index: true,             element: <Navigate to="posts" replace /> },
          { path: 'posts',           element: <BlogPostsPage /> },
          { path: 'posts/new',       element: <BlogPostEditorPage /> },
          { path: 'posts/:postId',   element: <BlogPostEditorPage /> }
        ]
      },
      { path: 'backlog',             element: <BacklogPage /> },
      { path: 'files/upload',        element: <FileUploadPage /> },

      // Back-compat redirects
      { path: 'fetching',            element: <Navigate to="/models" replace /> },
      { path: 'click-stats',         element: <Navigate to="/traffic" replace /> },
      { path: 'promoted-clicks',     element: <Navigate to="/traffic" replace /> },

      { path: '*', element: <Navigate to="/" replace /> }
    ]
  },
  { path: '*', element: <Navigate to="/" replace /> }
]);
